import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, Calendar, GripVertical, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "./ImageUploader";


export default function MusicEventsTab({ events, onRefresh }) {
  const [form, setForm] = useState({
    title: "",
    venue: "",
    city: "",
    date: "",
    time: "",
    ticket_url: "",
    description: "",
    details: "",
    location_url: "",
    image: "",
    order: 0,
    active: true,
    cancelled: false
  });
  const [editing, setEditing] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const sortedEvents = [...events].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminApi.put(`/music/events/${editing.id}`, form);
        toast.success("Evento actualizado");
      } else {
        await adminApi.post(`/music/events`, form);
        toast.success("Evento creado");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar evento");
    }
  };

  const handleEdit = (event) => {
    setEditing(event);
    setForm({
      title: event.title,
      venue: event.venue,
      city: event.city,
      date: event.date,
      time: event.time || "",
      ticket_url: event.ticket_url || "",
      description: event.description || "",
      details: event.details || "",
      location_url: event.location_url || "",
      image: event.image || "",
      order: event.order || 0,
      active: event.active,
      cancelled: event.cancelled || false
    });
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este evento?")) return;
    try {
      await adminApi.delete(`/music/events/${id}`);
      toast.success("Evento eliminado");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleCancelled = async (event) => {
    try {
      await adminApi.put(`/music/events/${event.id}`, { cancelled: !event.cancelled });
      toast.success(event.cancelled ? "Evento reactivado" : "Evento marcado como cancelado");
      onRefresh();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ title: "", venue: "", city: "", date: "", time: "", ticket_url: "", description: "", details: "", location_url: "", image: "", order: 0, active: true, cancelled: false });
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newEvents = [...sortedEvents];
    const [draggedItem] = newEvents.splice(draggedIndex, 1);
    newEvents.splice(dropIndex, 0, draggedItem);

    try {
      await Promise.all(
        newEvents.map((event, index) => 
          adminApi.put(`/music/events/${event.id}`, { order: index })
        )
      );
      toast.success("Orden actualizado");
      onRefresh();
    } catch (error) {
      toast.error("Error al reordenar");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-black mb-4 flex items-center gap-2">
          <Calendar className="text-[#E91E8C]" />
          {editing ? "Editar Evento" : "Nuevo Evento"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Título del Evento *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="Concierto en vivo"
              required
              className="bg-gray-50 border-gray-200 text-black"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Venue *</label>
              <Input
                value={form.venue}
                onChange={(e) => setForm({...form, venue: e.target.value})}
                placeholder="Foro Sol"
                required
                className="bg-gray-50 border-gray-200 text-black"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Ciudad *</label>
              <Input
                value={form.city}
                onChange={(e) => setForm({...form, city: e.target.value})}
                placeholder="CDMX"
                required
                className="bg-gray-50 border-gray-200 text-black"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Fecha *</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({...form, date: e.target.value})}
                required
                className="bg-gray-50 border-gray-200 text-black"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Hora</label>
              <Input
                value={form.time}
                onChange={(e) => setForm({...form, time: e.target.value})}
                placeholder="20:00"
                className="bg-gray-50 border-gray-200 text-black"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">URL de Boletos</label>
            <Input
              value={form.ticket_url}
              onChange={(e) => setForm({...form, ticket_url: e.target.value})}
              placeholder="https://ticketmaster.com/..."
              className="bg-gray-50 border-gray-200 text-black"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">URL de Ubicación (Google Maps / Venue)</label>
            <Input
              value={form.location_url}
              onChange={(e) => setForm({...form, location_url: e.target.value})}
              placeholder="https://maps.google.com/... o https://venue.com/..."
              className="bg-gray-50 border-gray-200 text-black"
            />
            <p className="text-xs text-gray-400 mt-1">Este link se abrirá al hacer clic en la ubicación del evento</p>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Descripción corta</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              placeholder="Breve descripción del evento"
              className="bg-gray-50 border-gray-200 text-black"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Detalles del evento (Lightbox)</label>
            <textarea
              value={form.details}
              onChange={(e) => setForm({...form, details: e.target.value})}
              placeholder="Información adicional: merch disponible, firma de autógrafos, meet & greet, etc."
              rows={4}
              className="w-full bg-gray-50 border border-gray-200 text-black rounded-md p-3 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Esta información se muestra en el lightbox al hacer clic en el evento</p>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Imagen</label>
            <ImageUploader
              value={form.image}
              onChange={(url) => setForm({...form, image: url})}
              category="thumbnails"
              recommendedSize="Tamaño recomendado: 400x400px"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({...form, active: e.target.checked})}
              className="rounded"
            />
            <label className="text-sm text-gray-600">Activo (visible en la página)</label>
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="bg-[#E91E8C] hover:bg-[#E91E8C]/90 text-white flex-1">
              <Save size={16} className="mr-2" />
              {editing ? "Actualizar" : "Crear Evento"}
            </Button>
            {editing && (
              <Button type="button" variant="outline" onClick={resetForm}>
                <X size={16} className="mr-1" /> Cancelar
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-black mb-2">Eventos ({events.length})</h2>
        <p className="text-sm text-gray-500 mb-4">Arrastra para reordenar</p>
        
        {sortedEvents.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay eventos</p>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {sortedEvents.map((event, index) => (
              <div
                key={event.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-move ${
                  dragOverIndex === index ? 'bg-pink-100 border-2 border-[#E91E8C]' : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                } ${draggedIndex === index ? 'opacity-50' : ''} ${!event.active ? 'opacity-60' : ''} ${event.cancelled ? 'bg-red-50' : ''}`}
              >
                <GripVertical size={20} className="text-gray-400" />
                
                {/* Thumbnail pequeño con estado cancelado */}
                {event.image && (
                  <div className="relative w-12 h-12 flex-shrink-0 rounded overflow-hidden">
                    <img 
                      src={event.image} 
                      alt=""
                      className={`w-full h-full object-cover ${event.cancelled ? 'grayscale' : ''}`}
                    />
                    {event.cancelled && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-red-600 text-white text-[6px] font-bold px-1 py-0.5 -rotate-12">
                          CANCELADO
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm truncate ${event.cancelled ? 'text-red-600 line-through' : 'text-black'}`}>
                    {event.title}
                    {event.cancelled && <span className="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded no-underline">CANCELADO</span>}
                  </p>
                  <p className="text-xs text-gray-500">{event.date} • {event.venue}, {event.city}</p>
                </div>
                
                {/* Toggle Cancelado */}
                <div className="flex items-center gap-1 mr-2">
                  <button
                    onClick={() => handleToggleCancelled(event)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      event.cancelled 
                        ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-red-500'
                    }`}
                    title={event.cancelled ? "Reactivar evento" : "Marcar como cancelado"}
                  >
                    <Ban size={14} />
                  </button>
                </div>
                
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(event)} className="h-8 w-8 p-0">
                    <Edit2 size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(event.id)} className="h-8 w-8 p-0 text-red-500">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
